import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Link as NavLink } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  DataListItem,
  Illustration,
  Input,
  Link,
  Text,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { Description } from 'components/text/Description'
import {
  PayoutsFeeEstimateParams,
  usePayoutsFeeEstimateQuery,
} from 'hooks/queries/usePayoutsFeeEstimateQuery'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import {
  BaseErrorResponse,
  BeneficiaryTransferDestination,
  BeneficiaryType,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'
import { Currencies } from 'types/types'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import { getBeneficiaryName } from 'utils/beneficiaries'
import {
  formatString,
  generateReference,
  roundNumberWithCommas,
  stringToDecimal,
} from 'utils/utils'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../../Pay/atoms/atoms'
import { getFiatSendFeePayload } from '../../Pay/utils/payFormUtils'
import { FeeErrors, MAX_GBP_PAYMENT_AMOUNT } from '../constants'
import { FiatSendFormKeys } from '../form/fiatSendFormSchema'

interface PaymentDetailsProps {
  setStep: React.Dispatch<React.SetStateAction<FiatSendStep>>
}

export const PaymentDetails = ({ setStep }: PaymentDetailsProps) => {
  const { t } = useTranslation()
  const { enableAchSameDayPayments, enableCustomerFee } = useFeatureFlags()

  const [walletAction] = useContext(WalletActionContext)

  const sourceWalletAtom = useAtomValue(selectedWalletAtom)
  const beneficiaryAtom = useAtomValue(selectedFiatBeneficiaryAtom)
  const formAtom = useAtomValue(fiatSendFormAtom)

  const [showErrorState, _setShowErrorState] = useState(false)

  const { formValues, errors, register, setValue, trigger, setError, clearErrors } = formAtom || {}

  useEffect(() => {
    setValue(
      'paymentReference',
      beneficiaryAtom?.beneficiaryPaymentReference ?? generateReference()
    )
  }, [beneficiaryAtom])

  const isLegacyPaymentMethod = !isNaN(Number(beneficiaryAtom?.reference))
  const isPayoutsV2Enabled =
    !isLegacyPaymentMethod && !!sourceWalletAtom?.supportedTransferDestinations?.length
  const request = getFiatSendFeePayload(formValues, sourceWalletAtom, isPayoutsV2Enabled)
  const payoutFeeEstimateParams: PayoutsFeeEstimateParams = {
    request,
    isEnabled: false,
    isThirdPartyPayment: beneficiaryAtom?.beneficiaryType === BeneficiaryType.THIRD_PARTY,
    isPayoutFeesV2: isPayoutsV2Enabled,
    transferDestination:
      beneficiaryAtom?.transferDestination ?? BeneficiaryTransferDestination.LOCAL,
  }

  const isBenficiaryPaymentMethodACH = beneficiaryAtom?.paymentMethod === USDBeneficiaryOptions.ACH

  const {
    isFetching: isFetchingFee,
    refetch: fetchFee,
    error: feeError,
  } = usePayoutsFeeEstimateQuery(payoutFeeEstimateParams)

  useEffect(() => {
    if (feeError) {
      const feeErrorResponse = feeError as BaseErrorResponse
      const isPaymentReferenceError = feeErrorResponse?.data?.details?.errors?.paymentReference

      if (feeErrorResponse?.data?.code === FeeErrors.BVNK_PAYMENT_0001) {
        setError('amount', { message: feeErrorResponse?.data?.message })
        return
      }

      if (feeErrorResponse?.data?.code === FeeErrors.BVNK_0000) {
        trigger('amount')
        return
      }

      if (isPaymentReferenceError) {
        trigger('paymentReference')
        return
      }

      setError('amount', { message: t('fetchingFeeError') })
      return
    }
    clearErrors('paymentReference')
    clearErrors('amount')
  }, [feeError])

  const walletTitle = useMemo(() => {
    return `${sourceWalletAtom?.walletName} | ${formatString(
      t('balanceWithCurrency'),
      roundNumberWithCommas(sourceWalletAtom?.balance),
      sourceWalletAtom?.currency
    )}`
  }, [sourceWalletAtom])

  const feeText = useMemo(() => {
    if ((!formValues?.amount || feeError || !formValues?.fee) && !isFetchingFee) {
      return `0.00 ${sourceWalletAtom?.currency}`
    }

    if (isFetchingFee) {
      return t('wallets.fiatSend.calculatingFee')
    }

    return `${roundNumberWithCommas(formValues?.fee)} ${sourceWalletAtom?.currency}`
  }, [isFetchingFee, formValues?.fee, sourceWalletAtom, formValues?.amount, feeError])

  const onInputChange = (field: FiatSendFormKeys, event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(field, event.target.value)
  }

  const handleOnContinueClick = async () => {
    clearErrors()
    const isValid = await trigger(['amount', 'paymentReference'])

    if (isValid) {
      setStep(FiatSendSteps.CONFIRMATION)
    }
  }

  const handleOnCancelClick = () => {
    walletAction?.onClose()
  }

  const validateAmount = async (amount: string) => {
    const error = await checkMaxAmount(amount)

    if (error) {
      setError('amount', { message: error })
    } else {
      clearErrors('amount')
    }
  }

  const checkMaxAmount = async (amount: string) => {
    const customerFeeAmount = Number(formValues?.customerFee ?? 0)
    const total = Number(amount ?? 0) + customerFeeAmount

    const feeData = await fetchFee()
    const { maximumWithdrawalAmount } = feeData?.data || {}

    const amountErrorMaximumFees =
      feeData &&
      sourceWalletAtom &&
      formatString(
        t('amountErrorMaximumFees'),
        roundNumberWithCommas(maximumWithdrawalAmount),
        sourceWalletAtom?.currency
      )

    if (Number(total) > maximumWithdrawalAmount) {
      return amountErrorMaximumFees
    }

    setValue('fee', feeData?.data?.fee.toString())
  }

  const amountOnBlurHandler = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    const amount = Number(event?.target?.value ?? 0)
    const customerFeeAmount = Number(formValues?.customerFee ?? 0)
    const total = amount + customerFeeAmount
    const isGbpPayment = sourceWalletAtom?.currency === Currencies.GBP

    if (!isNaN(total) && isGbpPayment && total > MAX_GBP_PAYMENT_AMOUNT) {
      setError('amount', { message: t('wallets.fiatSend.maximumGbpAmount') })
      return
    }

    event?.target?.value && validateAmount(stringToDecimal(event?.target?.value))
  }

  useEffect(() => {
    const shouldRefetchFee = !!formValues?.amount && !isNaN(Number(formValues?.amount))

    if (shouldRefetchFee) {
      validateAmount(formValues?.amount)
    }
  }, [formValues?.amount, formValues?.achSameDay])

  const isContinueButtonDisabled = useMemo(() => {
    return !formValues.amount || !formValues.paymentReference || !!errors?.amount
  }, [formValues.amount, formValues.paymentReference, errors?.amount])

  const isCustomerFeeEnabled = useMemo(() => {
    return !!walletAction?.meta?.customerWallet && enableCustomerFee
  }, [walletAction?.meta?.customerWallet])

  return (
    <Box paddingT={24} className="max-h-[75vh]">
      {showErrorState && (
        <StatusCard
          customIcon={<Illustration name="CloudLightning" color="error" />}
          headline={t('defaultError')}
        >
          <Description>
            {`${t('requestErrors.comeBackLater')} `}
            <Link href={window.location.href}>{t('requestErrors.refreshPage')}</Link>
          </Description>
        </StatusCard>
      )}
      {!showErrorState && (
        <Box paddingB={16}>
          <DataListItem
            id="wallet"
            title={t('wallets.fiatSend.sendingFromWallet')}
            BodyAvatar={<Avatar type="currency" name={sourceWalletAtom?.currency} size="small" />}
            body={walletTitle}
            layout={'vertical'}
          />

          <DataListItem
            id="beneficiary"
            title={t('wallets.fiatSend.sendingTo')}
            BodyAvatar={<Avatar type="entity" name={'A'} size="small" />}
            body={`${getBeneficiaryName(beneficiaryAtom)} | ${ellipseMiddleText(beneficiaryAtom?.fiat?.accountNumber, 6, 6)}`}
            layout={'vertical'}
          />

          <Box flexCol paddingY={16} width="full" gapY={16}>
            <Input
              {...register('amount')}
              label="Amount"
              placeholderText={'0'}
              onChange={e => onInputChange('amount', e)}
              testId="fiat-send-amount"
              type="text"
              inputMode="numeric"
              onBlur={amountOnBlurHandler}
              loading={isFetchingFee}
              rightSuffixText={sourceWalletAtom?.currency}
              error={!!errors.amount?.message}
              errorText={errors.amount?.message}
            />

            {isCustomerFeeEnabled && (
              <Input
                {...register('customerFee')}
                label={t('wallets.fiatSend.customerFee')}
                placeholderText={'0'}
                onChange={e => onInputChange('customerFee', e)}
                testId="fiat-customer-fee"
                type="text"
                inputMode="numeric"
                optionalText={t('optional')}
                rightSuffixText={sourceWalletAtom?.currency}
                error={!!errors.customerFee?.message}
                errorText={errors.customerFee?.message}
              />
            )}

            <Input
              {...register('paymentReference')}
              label="Payment reference"
              onChange={e => onInputChange('paymentReference', e)}
              onBlur={() => trigger('paymentReference')}
              testId="fiat-send-payment-reference"
              error={!!errors.paymentReference?.message}
              errorText={errors.paymentReference?.message}
            />

            {isBenficiaryPaymentMethodACH && enableAchSameDayPayments && (
              <Box paddingT={12} paddingB={8}>
                <Checkbox
                  {...register('achSameDay')}
                  id="ach-sameday-checkbox"
                  testId="ach-sameday-checkbox"
                  checked={formValues?.achSameDay}
                  onCheckedChange={() => setValue('achSameDay', !formValues?.achSameDay)}
                  label={t('wallets.fiatSend.makeAchSameDayPayment')}
                >
                  <Box flex>
                    <Text variant="caption-text" color="faint" className="pr-1">
                      {t('wallets.fiatSend.subjectToCutOffTimes')}
                    </Text>
                    <Text variant="caption-text" color="feature-accent" className="pr-1">
                      <NavLink to={process.env.VA_CUT_OFF_TIMES_LEARN_MORE} target="_blank">
                        {t('learnMore')}
                      </NavLink>
                    </Text>
                  </Box>
                </Checkbox>
              </Box>
            )}

            <DataListItem
              key={feeText}
              id="transaction-fee"
              title={t('wallets.fiatSend.transactionFee')}
              body={feeText}
              layout={'vertical'}
            />
          </Box>

          <Box flex justify="between" paddingT={20}>
            <Button testId="cancel-btn" variant="secondary" onClick={handleOnCancelClick}>
              {t('cancel')}
            </Button>
            <Button
              testId="continue-btn"
              onClick={handleOnContinueClick}
              disabled={isContinueButtonDisabled}
              loading={isFetchingFee}
            >
              {t('continue')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
